/* ==========================================================
   Jé Vue · House eye — the living background eye
   The blink is the approved entrance blink: the same shader,
   eyelid curves, lid sheet and timing, copied here unchanged so
   the approved entrance files stay untouched. The House eye
   artwork shares the entrance eye's lids, lashes and frame;
   only the iris and pupil differ.
   Exposes window.JVHouseEye = { start(), setActive(bool) }.
   ========================================================== */
(function(){
'use strict';
const S = 1254;
let CURVES='';   // read at start() from the theme's jevue-entrance.js
const BLINK={close:105,hold:45,open:250};          // ms, from the approved blink
const GAP=[2600,6000], DOUBLE_CHANCE=0.14, DOUBLE_GAP=140;
const VS=`attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}`;
const FS=`precision highp float;
uniform sampler2D uImg,uSheet,uCurve,uWel;uniform float uT,uW;
const float S=1254.,X0=95.,XE=1130.,X1=1192.,F=16.,G=18.,N=220.,B=70.,RISE=22.;
vec3 img(vec2 p){vec2 q=p/S;vec4 w=texture2D(uWel,q);return mix(texture2D(uImg,q).rgb,w.rgb,w.a*uW);}
float h16(float hi,float lo){return (hi*65280.+lo*255.)/32.;}
void main(){
  vec2 p=vec2(gl_FragCoord.x,S-gl_FragCoord.y);
  vec3 o=img(p);
  if(uT<=0.0||p.x<X0||p.x>X1){gl_FragColor=vec4(o,1.);return;}
  float cx=(floor(p.x)+.5)/S;
  vec4 r0=texture2D(uCurve,vec2(cx,.125)),r1=texture2D(uCurve,vec2(cx,.375)),r2=texture2D(uCurve,vec2(cx,.625)),r3=texture2D(uCurve,vec2(cx,.875));
  float u=h16(r0.r,r0.g),l=h16(r0.b,r0.a),k=h16(r1.r,r1.g),dc=h16(r1.b,r1.a),top=h16(r2.r,r2.g),bump=r2.b;
  vec3 gain=r3.rgb*2.55;
  float ring=1.-smoothstep(505.,564.,length(p-vec2(627.)));
  float D=dc*uT*ring;
  float e=u+D;
  vec3 c=o;
  if(p.y>=top&&p.y<e){
    float ext=min(0.3*D,0.55*max(k-top,0.));
    float span=max(k+ext-top,1e-3);
    vec3 st=img(vec2(p.x,top+(p.y-top)*(k-top)/span));
    if(p.y<k+ext){c=st;}
    else{
      vec3 rig=img(vec2(p.x,p.y-D));
      float depth=k+D-p.y;
      float sx=clamp(p.x-X0,0.,1119.);
      vec3 sh=texture2D(uSheet,vec2((sx+.5)/1120.,clamp(30.+depth,0.,599.)/600.)).rgb*gain;
      float fr=clamp(depth/max(D-ext,1.),0.,1.);
      sh*=0.9+0.3*sin(3.14159*fr);
      float w=smoothstep(0.,F,p.y-(k+D));w=mix(1.,w,clamp((D-ext)/F,0.,1.));
      vec3 warped=mix(sh,rig,w);
      c=mix(st,warped,clamp((p.y-(k+ext))/G,0.,1.));
    }
  }else if(p.y>=e&&p.x<=XE){
    float R=RISE*bump*uT*ring,lr=l-R;
    if(p.y>=lr&&p.y<l+N){
      float sy=p.y<lr+B?p.y+R:l+B+(p.y-lr-B)*(N-B)/(N-B+R);
      c=img(vec2(p.x,sy));
    }
    if(p.y<lr+1.){c*=1.-0.3*uT*exp(-(p.y-e)/10.);}
  }
  gl_FragColor=vec4(c,1.);
}`;
const easeInQuad=x=>x*x*(1.6-0.6*x);
const easeOutCubic=x=>1-Math.pow(1-x,3);
function lidAt(ms){
  const {close:a,hold:b,open:c}=BLINK;
  if(ms<0)return 0;
  if(ms<a)return easeInQuad(ms/a);
  if(ms<a+b)return 1;
  if(ms<a+b+c)return 1-easeOutCubic((ms-a-b)/c);
  return 0;
}
const ONE=BLINK.close+BLINK.hold+BLINK.open;
const A = p => (window.__JV_ASSETS && window.__JV_ASSETS[p]) || p;
const rm = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

let tainted = false, why = '';   // set if the eye image can only load without CORS (then it shows still, without blinking)
function load(src){
  const one = cors => new Promise((res,rej)=>{ const i=new Image(); if (cors) i.crossOrigin='anonymous'; i.onload=()=>res(i); i.onerror=rej; i.src=src; });
  return one(true).catch(() => one(false).then(i => { tainted = true; return i; }));
}

// Owner panel status line, so a preview can show whether the eye is blinking.
function report(state){
  try { const p = document.querySelector('#panel-owner .owner__p'); if (!p) return;
    let el = document.getElementById('jv-eye-status');
    if (!el){ el = document.createElement('p'); el.id = 'jv-eye-status'; el.className = 'owner__p'; p.after(el); }
    el.textContent = 'House eye: ' + state + (rm() ? ' · Reduce Motion is on, so it will not blink' : '');
  } catch(e){}
}

let cv=null, render=null, gl=null, started=false, active=false, visible=true, raf=0, nextAt=0, blinkStart=-1, dbl=false, lastT=-1;

function setup(im, sheet){
  cv = document.querySelector('[data-house-eye]');
  if (!cv) return false;
  try {
    if (tainted) throw new Error('eye image blocked for WebGL');
    if (!CURVES) throw new Error(why || 'eyelid data not found');
    gl = cv.getContext('webgl',{antialias:false,premultipliedAlpha:false,preserveDrawingBuffer:true});
    if (!gl) throw new Error('no webgl');
    const tex=(unit,src,w,h,data,nearest)=>{
      const t=gl.createTexture();gl.activeTexture(gl.TEXTURE0+unit);gl.bindTexture(gl.TEXTURE_2D,t);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL,false);gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL,false);
      gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL,gl.NONE);
      if(data)gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,w,h,0,gl.RGBA,gl.UNSIGNED_BYTE,data);
      else gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,src);
      const f=nearest?gl.NEAREST:gl.LINEAR;
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,f);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,f);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
    };
    const sh=(type,src)=>{const s=gl.createShader(type);gl.shaderSource(s,src);gl.compileShader(s);if(!gl.getShaderParameter(s,gl.COMPILE_STATUS))throw new Error(gl.getShaderInfoLog(s));return s;};
    const pr=gl.createProgram();
    gl.attachShader(pr,sh(gl.VERTEX_SHADER,VS));gl.attachShader(pr,sh(gl.FRAGMENT_SHADER,FS));gl.linkProgram(pr);
    if(!gl.getProgramParameter(pr,gl.LINK_STATUS))throw new Error(gl.getProgramInfoLog(pr));
    gl.useProgram(pr);
    gl.bindBuffer(gl.ARRAY_BUFFER,gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,1,1]),gl.STATIC_DRAW);
    const loc=gl.getAttribLocation(pr,'a');gl.enableVertexAttribArray(loc);gl.vertexAttribPointer(loc,2,gl.FLOAT,false,0,0);
    const raw=Uint8Array.from(atob(CURVES),ch=>ch.charCodeAt(0));
    const none=document.createElement('canvas'); none.width=none.height=4;   // no WELCOME in the House
    tex(0,im);tex(1,sheet);tex(2,null,S,4,raw,true);tex(3,none);
    gl.uniform1i(gl.getUniformLocation(pr,'uImg'),0);gl.uniform1i(gl.getUniformLocation(pr,'uSheet'),1);
    gl.uniform1i(gl.getUniformLocation(pr,'uCurve'),2);gl.uniform1i(gl.getUniformLocation(pr,'uWel'),3);
    const uT=gl.getUniformLocation(pr,'uT'), uW=gl.getUniformLocation(pr,'uW');
    gl.viewport(0,0,S,S);
    render=t=>{gl.uniform1f(uT,t);gl.uniform1f(uW,0);gl.drawArrays(gl.TRIANGLE_STRIP,0,4);};
    render(0);
    if (gl.getError()!==gl.NO_ERROR || gl.isContextLost()) throw new Error('webgl draw failed');
    report('blinking');
  } catch(err){
    report('still (' + ((err && err.message) || 'unknown') + ')');
    // Still-eye fallback: the open eye, no blinking.
    console.warn('[Jé Vue House eye] still eye:', err && err.message);
    gl=null; const c2=cv.cloneNode(false); cv.replaceWith(c2); cv=c2;
    const x=cv.getContext('2d'); x.drawImage(im,0,0,S,S); render=null;
  }
  cv.classList.add('is-ready');
  return true;
}

const gap = () => GAP[0] + Math.random()*(GAP[1]-GAP[0]);
function loop(now){
  raf = 0;
  if (!render) return;
  let t = 0;
  if (blinkStart >= 0){
    const ms = now - blinkStart;
    const len = dbl ? ONE*2 + DOUBLE_GAP - BLINK.open*0.5 : ONE;
    t = dbl ? Math.max(lidAt(ms), lidAt(ms - ONE - DOUBLE_GAP + BLINK.open*0.5)) : lidAt(ms);
    if (ms > len){ t = 0; blinkStart = -1; nextAt = now + gap(); }
  } else if (active && visible && !rm() && now >= nextAt){
    blinkStart = now; dbl = Math.random() < DOUBLE_CHANCE;
  }
  if (t !== lastT){ render(t); lastT = t; }
  // Idle: wake only when the next blink is due; mid-blink: every frame.
  if (blinkStart >= 0) raf = requestAnimationFrame(loop);
  else if (active && visible && !rm()) setTimeout(() => { if (!raf) raf = requestAnimationFrame(loop); }, Math.max(16, nextAt - performance.now()));
}
function kick(){ if (!raf && render) raf = requestAnimationFrame(loop); }

window.JVHouseEye = {
  /** load and draw the open eye; resolves true when shown */
  async start(){
    if (started) return !!cv; started = true;
    try {
      // In the Shopify theme the eyelid curves are read from the approved entrance script itself.
      if (!CURVES && window.__JV_ASSETS && window.__JV_ASSETS['assets/jevue-entrance.js']){
        // Shopify may minify the file, so look for the data itself rather than its variable name.
        try { const t = await fetch(window.__JV_ASSETS['assets/jevue-entrance.js'], { credentials:'omit' }).then(r => r.text());
              CURVES = (t.match(/["'`](W0BbQFtA[A-Za-z0-9+\/=]{1000,})["'`]/) || [])[1] || ''; } catch(e){ why = 'eyelid data could not load'; }
      }
      const [im, sheet] = await Promise.all([load(A('assets/house-eye.jpg')), load(A('assets/jevue-eye-lid-sheet.jpg'))]);
      const ok = setup(im, sheet);
      if (ok && active){ nextAt = Math.max(nextAt, performance.now() + 1800 + Math.random()*1600); kick(); }
      return ok;
    } catch(e){ console.warn('[Jé Vue House eye] eye files unavailable', e); report('missing (eye images did not load)'); return false; }
  },
  /** begin (or pause) natural blinking; the first blink comes after a settled pause */
  setActive(on){ active = !!on; if (on){ nextAt = performance.now() + 1800 + Math.random()*1600; kick(); } }
};
// Stop blinking while the hero is off screen or the tab is hidden.
document.addEventListener('visibilitychange', () => { visible = !document.hidden; if (visible){ nextAt = Math.max(nextAt, performance.now() + 1200); kick(); } });
if ('IntersectionObserver' in window){
  const el = document.querySelector('[data-house-eye]');
  el && new IntersectionObserver(es => { const v = es[0].isIntersecting; if (v !== visible){ visible = v && !document.hidden; if (visible){ nextAt = Math.max(nextAt, performance.now() + 900); kick(); } } }).observe(el);
}
})();
